import React, { useState } from 'react';
import { filesApi } from '../../api/files';
import '../../css/components/AttachmentList.css';

/**
 * Компонент отображения списка вложений
 * @param {Object} props
 * @param {Array} props.attachments - Массив вложений (FileResponse)
 * @param {string} props.baseUrl - Базовый URL API (по умолчанию берётся из конфига)
 * @param {boolean} props.showPreview - Показывать превью для аудио/видео (по умолчанию true)
 */
export default function AttachmentList({ attachments = [], baseUrl = 'https://api.hrnb.ru/api', showPreview = true }) {
    const [expandedMedia, setExpandedMedia] = useState(null);

    if (!Array.isArray(attachments) || attachments.length === 0) {
        return null;
    }

    const getFileTypeLabel = (fileType) => {
        const labels = {
            'IMAGE': 'Изображение',
            'VIDEO': 'Видео',
            'AUDIO': 'Аудио',
            'DOCUMENT': 'Документ',
            'TEXT': 'Текст',
            'ARCHIVE': 'Архив',
            'PRESENTATION': 'Презентация',
            'SPREADSHEET': 'Таблица'
        };
        return labels[fileType] || 'Файл';
    };

    const getFileTypeIcon = (fileType, contentType) => {
        if (fileType === 'IMAGE' || contentType?.startsWith('image/')) return '🖼️';
        if (fileType === 'VIDEO' || contentType?.startsWith('video/')) return '🎬';
        if (fileType === 'AUDIO' || contentType?.startsWith('audio/')) return '🎵';
        if (fileType === 'DOCUMENT' || contentType?.includes('pdf')) return '📄';
        if (fileType === 'TEXT') return '📝';
        if (fileType === 'ARCHIVE') return '📦';
        if (fileType === 'PRESENTATION') return '📊';
        if (fileType === 'SPREADSHEET') return '📈';
        return '📎';
    };

    const isImage = (fileType, contentType) => {
        return fileType === 'IMAGE' || contentType?.startsWith('image/');
    };

    const isVideo = (fileType, contentType) => {
        return fileType === 'VIDEO' || contentType?.startsWith('video/');
    };

    const isAudio = (fileType, contentType) => {
        return fileType === 'AUDIO' || contentType?.startsWith('audio/');
    };

    const handleDownload = async (fileId, fileName) => {
        try {
            const blob = await filesApi.download(fileId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || 'file';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('[AttachmentList] Download failed:', err);
            window.open(`${baseUrl}/files/${fileId}/download`, '_blank');
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes && bytes !== 0) return '';
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const toggleMediaExpand = (fileId) => {
        setExpandedMedia(expandedMedia === fileId ? null : fileId);
    };

    // Группируем файлы по типам
    const images = attachments.filter(a => isImage(a.fileType, a.contentType));
    const videos = attachments.filter(a => isVideo(a.fileType, a.contentType));
    const audios = attachments.filter(a => isAudio(a.fileType, a.contentType));
    const otherFiles = attachments.filter(a => !isImage(a.fileType, a.contentType) && !isVideo(a.fileType, a.contentType) && !isAudio(a.fileType, a.contentType));

    return (
        <div className="attachment-list">
            {images.length > 0 && (
                <div className="attachment-list__section">
                    <div className="attachment-list__grid">
                        {images.map((attachment) => (
                            <div key={attachment.id} className="attachment-list__image-wrapper">
                                <img
                                    src={`${baseUrl}/files/${attachment.id}/download`}
                                    alt={attachment.fileName}
                                    className="attachment-list__image"
                                    loading="lazy"
                                />
                                <div className="attachment-list__image-overlay">
                                    <button
                                        className="attachment-list__download-btn"
                                        onClick={() => handleDownload(attachment.id, attachment.fileName)}
                                        title="Скачать"
                                    >
                                        ⬇️ Скачать
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showPreview && videos.length > 0 && (
                <div className="attachment-list__section">
                    <h4 className="attachment-list__section-title">🎬 Видео</h4>
                    <div className="attachment-list__media-grid">
                        {videos.map((attachment) => (
                            <div key={attachment.id} className="attachment-list__media-wrapper">
                                {expandedMedia === attachment.id ? (
                                    <video
                                        controls
                                        className="attachment-list__video"
                                        src={`${baseUrl}/files/${attachment.id}/download`}
                                        onEnded={() => setExpandedMedia(null)}
                                    >
                                        Ваш браузер не поддерживает видео.
                                    </video>
                                ) : (
                                    <div
                                        className="attachment-list__media-preview"
                                        onClick={() => toggleMediaExpand(attachment.id)}
                                    >
                                        <div className="attachment-list__media-play-btn">▶️</div>
                                        <span className="attachment-list__media-name">{attachment.fileName}</span>
                                        <span className="attachment-list__media-size">{formatFileSize(attachment.fileSize)}</span>
                                    </div>
                                )}
                                <button
                                    className="attachment-list__media-download"
                                    onClick={() => handleDownload(attachment.id, attachment.fileName)}
                                    title="Скачать"
                                >
                                    ⬇️
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showPreview && audios.length > 0 && (
                <div className="attachment-list__section">
                    <h4 className="attachment-list__section-title">🎵 Аудио</h4>
                    <div className="attachment-list__audio-list">
                        {audios.map((attachment) => (
                            <div key={attachment.id} className="attachment-list__audio-item">
                                <audio
                                    controls
                                    className="attachment-list__audio"
                                    src={`${baseUrl}/files/${attachment.id}/download`}
                                >
                                    Ваш браузер не поддерживает аудио.
                                </audio>
                                <button
                                    className="attachment-list__audio-download"
                                    onClick={() => handleDownload(attachment.id, attachment.fileName)}
                                    title="Скачать"
                                >
                                    ⬇️
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {otherFiles.length > 0 && (
                <div className="attachment-list__section">
                    <h4 className="attachment-list__section-title">📎 Файлы</h4>
                    <div className="attachment-list__files">
                        {otherFiles.map((attachment) => (
                            <div
                                key={attachment.id}
                                className="attachment-list__file"
                                onClick={() => handleDownload(attachment.id, attachment.fileName)}
                            >
                                <span className="attachment-list__file-icon">
                                    {getFileTypeIcon(attachment.fileType, attachment.contentType)}
                                </span>
                                <div className="attachment-list__file-info">
                                    <span className="attachment-list__file-name">
                                        {attachment.fileName}
                                    </span>
                                    <span className="attachment-list__file-meta">
                                        {getFileTypeLabel(attachment.fileType)} • {formatFileSize(attachment.fileSize)}
                                    </span>
                                </div>
                                <span className="attachment-list__file-download">
                                    ⬇️
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!showPreview && (videos.length > 0 || audios.length > 0) && (
                <div className="attachment-list__section">
                    <div className="attachment-list__files">
                        {[...videos, ...audios].map((attachment) => (
                            <div
                                key={attachment.id}
                                className="attachment-list__file"
                                onClick={() => handleDownload(attachment.id, attachment.fileName)}
                            >
                                <span className="attachment-list__file-icon">
                                    {getFileTypeIcon(attachment.fileType, attachment.contentType)}
                                </span>
                                <div className="attachment-list__file-info">
                                    <span className="attachment-list__file-name">
                                        {attachment.fileName}
                                    </span>
                                    <span className="attachment-list__file-meta">
                                        {getFileTypeLabel(attachment.fileType)} • {formatFileSize(attachment.fileSize)}
                                    </span>
                                </div>
                                <span className="attachment-list__file-download">
                                    ⬇️
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
